import React from "react";
import "./Strategies.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faScaleBalanced,
  faEye,
  faHandshake,
  faLink,
  faCookieBite,
  faTriangleExclamation,
  faGlobe,
  faShieldHalved,
  faComments,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};

const strategyItems = [
  {
    icon: faScaleBalanced,
    pill: "Fairness",
    title: "Honest comparisons",
    description:
      "When we compare products or services, we explain criteria and limits. Rankings are not paid placement unless labeled as sponsored.",
    bullets: ["Criteria stated", "No fake endorsements", "Updates when models change"],
  },
  {
    icon: faEye,
    pill: "Clarity",
    title: "Visible business identity",
    description:
      "Who we are, how to contact us, and where to read policies are easy to find—important for advertising and consumer trust.",
    bullets: ["About + contact", "Legal hub", "Consistent branding"],
  },
  {
    icon: faHandshake,
    pill: "Brand respect",
    title: "Trademarks & platforms",
    description:
      "Third-party logos and platform marks belong to their owners. We use them accurately and never imply endorsement we do not have.",
    bullets: ["No impersonation", "Attribution where required", "Neutral tone"],
  },
  {
    icon: faLink,
    pill: "Disclosure",
    title: "Links & compensation",
    description:
      "Affiliate, referral, or sponsored links are disclosed in line with FTC guidance. Ads are labeled as advertising.",
    bullets: ["Clear labeling", "Editorial separation", "No hidden fees to readers"],
  },
  {
    icon: faCookieBite,
    pill: "Privacy",
    title: "Data minimization",
    description:
      "We describe cookies and analytics in our privacy policy. Forms collect only what we need to respond to you.",
    bullets: ["Privacy policy", "Opt-out notes", "Security basics"],
  },
  {
    icon: faTriangleExclamation,
    pill: "Safety",
    title: "Risk-aware guidance",
    description:
      "We flag sensitive verticals (health, finance, kids) where extra claims review or licensing may apply.",
    bullets: ["Vertical policies", "Substantiation", "Escalate to counsel"],
  },
  {
    icon: faGlobe,
    pill: "Locale",
    title: "Markets & locale",
    description:
      "We adapt tone, creative, and compliance notes to your primary markets. International campaigns may need localized creative, law, and platform settings.",
    bullets: ["Currency & pricing copy", "Regional privacy rules", "Export or sanctions flags"],
  },
  {
    icon: faShieldHalved,
    pill: "Security",
    title: "Account security",
    description:
      "We encourage least-privilege access to ad accounts, 2FA, and documented admin roles. We never ask for unrelated passwords.",
    bullets: ["MFA reminders", "Agency vs. owner access", "Phishing awareness"],
  },
  {
    icon: faComments,
    pill: "Feedback",
    title: "Client communication",
    description:
      "We document decisions in writing, welcome pushback on risky claims, and adjust when new facts emerge.",
    bullets: ["Shared notes", "Approval trails", "Escalation paths"],
  },
];

const Strategies = () => {
  return (
    <section className="strat" aria-label="Content principles">
      <div className="strat__wrap">
        <motion.div
          className="strat__head"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.35 }}
          variants={fadeUp}
        >
          <div>
            <div className="strat__badge">
              <span className="dot" aria-hidden="true" />
              Compliance-minded marketing
            </div>

            <h2 className="strat__title">
              Principles for <span>clients</span> and <span>ad platforms</span>.
            </h2>

            <p className="strat__sub">
              These pillars summarize how we try to align with honest advertising norms. They are not legal advice;
              consult counsel for regulated industries or complex campaigns.
            </p>
          </div>

          <div className="strat__note">
            <b>Google Ads &amp; similar</b>
            <p>
              Destination pages should match your ad promises, load securely (HTTPS), and avoid prohibited content.
              Keep privacy disclosures accurate if you measure users across sites.
            </p>
          </div>
        </motion.div>

        <div className="strat__grid">
          {strategyItems.map((item, index) => (
            <motion.div
              className="strat__card"
              key={item.title}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              variants={fadeUp}
              transition={{ delay: index * 0.04 }}
            >
              <div className="strat__top">
                <div className="strat__icon">
                  <FontAwesomeIcon icon={item.icon} />
                </div>
                <div className="strat__pill">{item.pill}</div>
              </div>

              <h3 className="strat__h3">{item.title}</h3>
              <p className="strat__p">{item.description}</p>

              <ul className="strat__bullets">
                {item.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Strategies;
